import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financialsApi, type FinancialsPeriod } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';
import { formatPeriod } from '@/lib/wipHistory';

// Month-end close (#613), on Reports → Work-in-Progress. Accounting clicks Close once
// Intacct has closed the month: every job's final figures are stored, and the month's
// view on each job stops re-reading the GL. Shown only to people who hold
// financials.close_month (the page decides); the API enforces it either way.

const css = `
  .mc-card { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:var(--ew-s-4) var(--ew-s-5); margin-bottom:var(--ew-s-4); }
  .mc-card h3 { font:var(--ew-fw-semibold) 14px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-dark); margin:0 0 6px; }
  .mc-note { font-size:12.5px; line-height:1.5; color:var(--ew-n-600); margin:0 0 var(--ew-s-3); }
  .mc-row { display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding:8px 0; border-bottom:1px solid #F2F2F1; font-size:13px; }
  .mc-row:last-child { border-bottom:none; }
  .mc-month { font-weight:600; min-width:92px; }
  .mc-meta { color:var(--ew-n-500); flex:1 1 220px; }
  .mc-chip { font:var(--ew-fw-semibold) 10.5px/1 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; border-radius:3px; padding:4px 8px; }
  .mc-chip.open { background:#FBEEE1; color:#8A4E12; }
  .mc-chip.closed { background:var(--ew-green-50); color:var(--ew-green-700); }
  .mc-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:7px 13px; cursor:pointer; border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .mc-btn.primary { background:var(--ew-green); border-color:var(--ew-green); color:#fff; }
  .mc-btn:disabled { opacity:.5; cursor:not-allowed; }
  .mc-msg { font-size:12.5px; margin-top:10px; }
  .mc-msg.ok { color:var(--ew-green-700); font-weight:600; }
  .mc-msg.err { color:#B8321C; font-weight:600; }
`;

/** The server's own words when it refused, else a plain fallback. */
function serverMessage(e: unknown): string {
  const m = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof m === 'string' && m) return m;
  if (Array.isArray(m) && m.length) return m.join(' ');
  return 'Something went wrong. Nothing was changed; try again.';
}

export default function MonthClosePanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['financials', 'periods'], queryFn: () => financialsApi.periods(), staleTime: 30_000 });
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  // A close or reopen changes what every job's month view and history show.
  const refresh = () => qc.invalidateQueries({ queryKey: ['financials'] });

  const close = useMutation({
    mutationFn: (period: string) => financialsApi.closeMonth(period),
    onSuccess: (r) => {
      setMsg({
        tone: 'ok',
        text: `✓ ${formatPeriod(r.period)} closed. Final figures stored for ${r.frozen} job${r.frozen === 1 ? '' : 's'}`
          + (r.unreadable ? `; ${r.unreadable} job${r.unreadable === 1 ? ' has' : 's have'} no readable month-end freeze and ${r.unreadable === 1 ? 'was' : 'were'} skipped.` : '.'),
      });
      refresh();
    },
    // Refresh on failure too. Closing runs every job in one request, so a slow one can
    // fail at the gateway after the server has finished; the list then shows the true
    // state instead of leaving a closed month looking open.
    onError: (e) => {
      setMsg({ tone: 'err', text: `${serverMessage(e)} If it timed out, check the list below: the month may have closed.` });
      refresh();
    },
  });
  const reopen = useMutation({
    mutationFn: (period: string) => financialsApi.reopenMonth(period),
    onSuccess: (_r, period) => { setMsg({ tone: 'ok', text: `✓ ${formatPeriod(period)} reopened.` }); refresh(); },
    onError: (e) => { setMsg({ tone: 'err', text: serverMessage(e) }); refresh(); },
  });
  const busy = close.isPending || reopen.isPending;

  const onClose = (p: FinancialsPeriod) => {
    setMsg(null);
    const ok = window.confirm(
      `Close ${formatPeriod(p.period)}?\n\n`
      + `Do this only after Intacct has closed the month. It stores the final figures for ${p.jobs} job${p.jobs === 1 ? '' : 's'}: `
      + `cost from every entry dated through month end, and the contract, estimate and purchase orders as frozen at month end. `
      + `After that the month no longer changes when Intacct does.\n\nYou can reopen it later.`,
    );
    if (ok) close.mutate(p.period);
  };
  const onReopen = (p: FinancialsPeriod) => {
    setMsg(null);
    const ok = window.confirm(
      `Reopen ${formatPeriod(p.period)}?\n\n`
      + `The stored final figures are discarded and the month goes back to reading cost from Intacct as it stands today.`,
    );
    if (ok) reopen.mutate(p.period);
  };

  const periods = q.data?.periods ?? [];

  return (
    <div className="mc-card">
      <style>{css}</style>
      <h3>Month-end close</h3>
      <p className="mc-note">
        Close a month once Intacct has closed it. Each job's month then shows final figures that no longer change.
        Until then a month&rsquo;s cost keeps updating as Intacct posts entries dated in it.
      </p>

      {q.isLoading && <div className="mc-note">Loading months…</div>}
      {q.isError && <div className="mc-msg err">Couldn&rsquo;t load the months.</div>}
      {q.data && !q.data.closeReady && (
        <div className="mc-msg err">
          Closing a month isn&rsquo;t set up on this database yet. It needs a one-time database update first.
        </div>
      )}
      {q.data && periods.length === 0 && q.data.closeReady && (
        <div className="mc-note">No month has been frozen yet, so there is nothing to close.</div>
      )}

      {periods.map((p) => (
        <div className="mc-row" key={p.period}>
          <span className="mc-month">{formatPeriod(p.period)}</span>
          {p.closed
            ? <span className="mc-chip closed">Closed</span>
            : <span className="mc-chip open">Open</span>}
          <span className="mc-meta">
            {p.closed
              ? `Closed${p.closed.by ? ` by ${p.closed.by}` : ''} on ${fmtDateTime(p.closed.at)}${p.closed.jobsFrozen != null ? ` · ${p.closed.jobsFrozen} job${p.closed.jobsFrozen === 1 ? '' : 's'} frozen` : ''}`
              : `${p.jobs} job${p.jobs === 1 ? '' : 's'} frozen at month end`}
          </span>
          {p.closed
            ? <button className="mc-btn" disabled={busy || !q.data?.closeReady} onClick={() => onReopen(p)}>Reopen</button>
            : <button className="mc-btn primary" disabled={busy || !q.data?.closeReady} onClick={() => onClose(p)}>
                {close.isPending && close.variables === p.period ? 'Closing…' : `Close ${formatPeriod(p.period)}`}
              </button>}
        </div>
      ))}

      {msg && <div className={`mc-msg ${msg.tone}`} role={msg.tone === 'err' ? 'alert' : 'status'}>{msg.text}</div>}
    </div>
  );
}
